import { useRef, useState } from 'react'
import {
  Combobox as HUICombobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react'
import { ChevronDown, X } from 'lucide-react'
import { cn } from './cn'

export interface ComboboxItem {
  /** onChange で返る値 */
  value: string
  /** 入力欄・候補に表示する文字列 */
  label: string
  /** label 以外の絞り込み対象（読みがな・別名など） */
  keywords?: string[]
}

export interface ComboboxProps {
  /** 選択中の値。未選択は空文字 */
  value: string
  /** 選択・解除で発火。解除時は空文字 */
  onChange: (value: string) => void
  items: ComboboxItem[]
  /** input の id（label の htmlFor 対象） */
  id?: string
  placeholder?: string
  disabled?: boolean
  /** 検証エラー状態。aria-invalid と danger ボーダーを付与 */
  error?: boolean
  /** label 要素を持たない場合の accessible name */
  'aria-label'?: string
  'aria-describedby'?: string
  /** 候補が 0 件のときの文言 */
  emptyMessage?: string
  /** 選択解除ボタンの accessible name */
  clearLabel?: string
  /** ラッパの追加クラス（append-only） */
  className?: string
}

// border/focus/error 表現は本コンポーネントが確定（Input/Select と同一トークン）。
const BASE =
  'w-full pl-4 pr-16 py-2 rounded-field border-[1.5px] bg-white transition-colors ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

const NORMAL = 'border-[#ddd] focus:border-primary focus-visible:ring-primary'
const ERROR = 'border-danger focus:border-danger focus-visible:ring-danger'

const ICON_BUTTON =
  'flex items-center justify-center text-gray-400 hover:text-gray-600 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'

/**
 * 絞り込み検索の突き合わせ用に表記ゆれを吸収する。
 * 候補側・入力側の双方に同じ変換をかけるため、変換の中身より「両側で同じ」ことが重要。
 * - NFKC: 全角英数・半角カナを正規化（ＣｏＣ→coc、ｸﾄｩﾙﾌ→クトゥルフ）
 * - カタカナ→ひらがな: IME 変換前のかな入力（しのびがみ）で「シノビガミ」を引けるようにする
 * - 記号・空白・長音を除去: 「ソード・ワールド」を「そーどわーるど」「そどわ」でも引けるようにする
 */
function normalizeForSearch(text: string): string {
  return text
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[ァ-ヶ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0x60))
    .replace(/[\s・.,()（）「」［］[\]ー―‐~〜_/-]/g, '')
}

/**
 * 検索できる単一選択ドロップダウン（select + 部分一致フィルタ）。
 * 候補数が多く目視で探しにくい一覧向け。キーボード操作・aria 配線は Headless UI が担保する。
 * label 関連付け（htmlFor/id もしくは aria-label）は利用側責務。
 */
export function Combobox({
  value,
  onChange,
  items,
  id,
  placeholder,
  disabled = false,
  error = false,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  emptyMessage = '一致する候補がありません',
  clearLabel = '選択を解除',
  className,
}: ComboboxProps) {
  const [query, setQuery] = useState('')
  const toggleButtonRef = useRef<HTMLButtonElement>(null)

  const normalizedQuery = normalizeForSearch(query)
  const filtered = normalizedQuery
    ? items.filter((item) =>
        [item.label, ...(item.keywords ?? [])].some((candidate) =>
          normalizeForSearch(candidate).includes(normalizedQuery),
        ),
      )
    : items

  const selected = items.find((item) => item.value === value)

  return (
    // 未選択は空文字で受けるが、Headless UI 側は null が未選択（空文字の item と区別できないため）。
    <HUICombobox
      value={value === '' ? null : value}
      // 確定と同時に絞り込みを解除する。残すと入力欄には選択中の label が出ているのに
      // 次に開いた候補だけが古い検索語で絞られたままになる。
      onChange={(next: string | null) => {
        onChange(next ?? '')
        setQuery('')
      }}
      disabled={disabled}
      immediate
    >
      {({ open }) => (
        <div className={cn('relative', className)}>
          <ComboboxInput
            id={id}
            className={cn(BASE, error ? ERROR : NORMAL)}
            // 選択済みの表示は常に item の label に戻す（入力途中の文字を残さない）
            displayValue={() => selected?.label ?? ''}
            onChange={(e) => setQuery(e.target.value)}
            // 閉じたら絞り込みを解除し、次に開いたとき全件から選べるようにする
            onBlur={() => setQuery('')}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setQuery('')
            }}
            // immediate はフォーカス時にしか開かないため、選択直後（フォーカス保持中）の
            // クリックでは開かない。select と同じ感覚で開けるようトグルへ委譲する。
            onClick={() => {
              if (!open) toggleButtonRef.current?.click()
            }}
            placeholder={placeholder}
            aria-invalid={error || undefined}
            aria-label={ariaLabel}
            aria-describedby={describedBy}
            autoComplete="off"
          />

          <div className="absolute inset-y-0 right-0 flex items-center gap-1 pr-2">
            {selected && !disabled && (
              <button
                type="button"
                // 入力欄の表示は displayValue が selected を見て戻すため、query も空に揃える
                onClick={() => {
                  onChange('')
                  setQuery('')
                }}
                aria-label={clearLabel}
                className={cn(ICON_BUTTON, 'h-6 w-6')}
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            <ComboboxButton
              ref={toggleButtonRef}
              className={cn(ICON_BUTTON, 'h-6 w-6')}
              aria-label="候補を表示"
            >
              <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </ComboboxButton>
          </div>

          <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-field border-[1.5px] border-[#ddd] bg-white py-1 shadow-card focus:outline-none">
            {filtered.length === 0 ? (
              // listbox の子は option のみが正なので、文言行は presentation にして支援技術から除く
              <li role="presentation" className="px-4 py-2 text-sm text-gray-500">
                {emptyMessage}
              </li>
            ) : (
              filtered.map((item) => (
                <ComboboxOption
                  key={item.value}
                  value={item.value}
                  className="cursor-pointer px-4 py-2 text-sm data-[focus]:bg-primary data-[focus]:text-white data-[selected]:font-semibold"
                >
                  {item.label}
                </ComboboxOption>
              ))
            )}
          </ComboboxOptions>
        </div>
      )}
    </HUICombobox>
  )
}
